import React, { Suspense, useEffect } from "react";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import ScrollToTop from "./ScrollToTop.js";
import ProtectedRoute from "./ProtectedRoute.js";
import { useMagicLogin } from "./hooks/useMagicLogin.js";
import GTMTracker from "./GTMTracker.js";

const ServerError = React.lazy(() => import("./pages/ServerError.js"));
const Layout = React.lazy(() => import("./components/Layouts/Core/Index.js"));
// ACCOUNTS
const Login = React.lazy(() => import("./pages/Accounts/Login/Index.js"));
const Register = React.lazy(() => import("./pages/Accounts/Register/Index.js"));
const ResetPassword = React.lazy(
  () => import("./pages/Accounts/ForgetPassword/Index.js"),
);

const MessagesLandingPage = React.lazy(
  () => import("./pages/Panel/Komunikator/Komunikator.js"),
);
// UserPanel
const Panel = React.lazy(() => import("./pages/Panel/Home/Index.js"));
const Ankiety = React.lazy(() => import("./pages/Panel/Ankiety/Index.js"));
const Ankieta1 = React.lazy(() => import("./pages/Panel/Ankiety/Ankieta1.js"));
const Ankieta2 = React.lazy(() => import("./pages/Panel/Ankiety/Ankieta2.js"));
const Ankieta3 = React.lazy(() => import("./pages/Panel/Ankiety/Ankieta3.js"));
const Ankieta3Wynik = React.lazy(
  () => import("./pages/Panel/Ankiety/Ankieta3Wynik.js"),
);

const Komunikator = React.lazy(
  () => import("./pages/Panel/Komunikator/Index.js"),
);
const Inspieracje = React.lazy(
  () => import("./pages/Panel/Inspiracje/Index.js"),
);
const InspirationsDetail = React.lazy(
  () => import("./pages/Panel/Inspiracje/InspirationsDetailPage.js"),
);
const Zdjecia = React.lazy(() => import("./pages/Panel/Zdjecia/Zdjecia.js"));
const ZdjeciaDetail = React.lazy(
  () => import("./pages/Panel/Zdjecia/ZdjeciaDetal.js"),
);
const ZdjeciaDetailImage = React.lazy(
  () => import("./pages/Panel/Zdjecia/ZdjeciaDetalImage.js"),
);
const Projekt = React.lazy(() => import("./pages/Panel/Projekt/Home.js"));
const ProjektListaZakupow = React.lazy(
  () => import("./pages/Panel/Projekt/ListaZakupow.js"),
);
const ProjektRysunkiTechniczne = React.lazy(
  () => import("./pages/Panel/Projekt/RysunkiTechniczne.js"),
);
const ProjektRysunkiTechniczneDetail = React.lazy(
  () => import("./pages/Panel/Projekt/RysunkiTechniczneDetal.js"),
);
const ProjektRysunkiTechniczneDetailImage = React.lazy(
  () => import("./pages/Panel/Projekt/RysunkiTechniczneDetalImage.js"),
);
const ProjektSprzetAGD = React.lazy(
  () => import("./pages/Panel/Projekt/SprzetAGDRTV.js"),
);
const ProjektUkladFunkcji = React.lazy(
  () => import("./pages/Panel/Projekt/UkladFunkcji.js"),
);
const ProjektUkladFunkcjiDetail = React.lazy(
  () => import("./pages/Panel/Projekt/UkladFunkcjiDetal.js"),
);
const ProjektUkladFunkcjiDetailImage = React.lazy(
  () => import("./pages/Panel/Projekt/UkladFunkcjiDetalImage.js"),
);
const ProjektWizualizacja = React.lazy(
  () => import("./pages/Panel/Projekt/Wizualizacja.js"),
);
const ProjektWizualizacjaDetail = React.lazy(
  () => import("./pages/Panel/Projekt/WizualizacjaDetal.js"),
);
const ProjektWizualizacjaDetailImage = React.lazy(
  () => import("./pages/Panel/Projekt/WizualizacjaDetalImage.js"),
);
const Realizacja = React.lazy(() => import("./pages/Panel/Realizacja/Home.js"));
const RealizacjaPoprawki = React.lazy(
  () => import("./pages/Panel/Realizacja/Poprawki.js"),
);
const RealizacjaPoprawkiDetail = React.lazy(
  () => import("./pages/Panel/Realizacja/PoprawkaDetal.js"),
);

const RealizacjaZakresPrac = React.lazy(
  () => import("./pages/Panel/Realizacja/ZakresPrac.js"),
);
const RealizacjaWycena = React.lazy(
  () => import("./pages/Panel/Realizacja/Wycena.js"),
);

const BudgetExpenses = React.lazy(
  () => import("./pages/Panel/BudgetExpenses/Home.js"),
);
const BudgetExpensesMaterialy = React.lazy(
  () => import("./pages/Panel/BudgetExpenses/Materialy.js"),
);
const BudgetExpensesWykonawcy = React.lazy(
  () => import("./pages/Panel/BudgetExpenses/Wykonawcy.js"),
);
const BudgetExpensesZakupy = React.lazy(
  () => import("./pages/Panel/BudgetExpenses/Zakupy.js"),
);

const Dokumenty = React.lazy(() => import("./pages/Panel/Dokumenty/Home.js"));
const DokumentyWykonawcy = React.lazy(
  () => import("./pages/Panel/Dokumenty/Wykonawcy.js"),
);
const DokumentyWyposazenie = React.lazy(
  () => import("./pages/Panel/Dokumenty/Wyposazenie.js"),
);
const DokumentyGwarancje = React.lazy(
  () => import("./pages/Panel/Dokumenty/Gwarancje.js"),
);
const KontaktPanel = React.lazy(() => import("./pages/Panel/Kontakt/Home.js"));
const Tasks = React.lazy(() => import("./pages/Panel/Todo/Index.js"));
const PanelUserSettings = React.lazy(
  () => import("./pages/Panel/Settings/UserSettings.js"),
);

// Admin Panel
const Admin = React.lazy(() => import("./pages/Admin/Home/Index.js"));
const AdminProjects = React.lazy(
  () => import("./pages/Admin/Projects/Index.js"),
);
const AdminUsers = React.lazy(() => import("./pages/Admin/Users/Index.js"));
const AdminArchitects = React.lazy(
  () => import("./pages/Admin/Architects/Index.js"),
);
const AdminWykonawcy = React.lazy(
  () => import("./pages/Admin/Wykonawcy/Index.js"),
);
const AdminArchitect = React.lazy(
  () => import("./pages/Admin/Architect/Index.js"),
);
const AdminUser = React.lazy(() => import("./pages/Admin/User/Index.js"));
const AdminWykonawca = React.lazy(
  () => import("./pages/Admin/Wykonawca/Index.js"),
);
const AdminPracownicy = React.lazy(
  () => import("./pages/Admin/Pracownicy/Index.js"),
);
const AdminPracownik = React.lazy(
  () => import("./pages/Admin/Pracownik/Index.js"),
);
const AdminSellers = React.lazy(() => import("./pages/Admin/Sellers/Index.js"));
const AdminSeller = React.lazy(() => import("./pages/Admin/Seller/Index.js"));
const AdminPayments = React.lazy(
  () => import("./pages/Admin/Payments/Index.js"),
);
const AdminFeedbacks = React.lazy(
  () => import("./pages/Admin/Feedbacks/Index.js"),
);
const AdminFeedback = React.lazy(
  () => import("./pages/Admin/Feedback/Index.js"),
);
const AdminHarmonogram = React.lazy(
  () => import("./pages/Admin/Scheduler/Index.js"),
);
const AdminSettingsUser = React.lazy(
  () => import("./pages/Admin/Settings/UserSettings.js"),
);

// Seller Panel
const Seller = React.lazy(() => import("./components/Layouts/Seller/Index.js"));

const SellerHome = React.lazy(() => import("./pages/Seller/Home/Index.js"));

const SellerClients = React.lazy(
  () => import("./pages/Seller/Clients/Index.js"),
);

const SellerSettlements = React.lazy(
  () => import("./pages/Seller/Settlements/Index.js"),
);

// Architekt
const ArchitectLayout = React.lazy(
  () => import("./components/Layouts/Architect/Index.js"),
);

const Architekt = React.lazy(() => import("./pages/Architect/Home/Index.js"));
const ArchitektAddUser = React.lazy(
  () => import("./pages/Architect/AddProject/Index.js"),
);
const ArchitektChecklist = React.lazy(
  () => import("./pages/Architect/Checklist/Index.js"),
);
const ArchitektTodo = React.lazy(
  () => import("./pages/Architect/Todo/Index.js"),
);
const ArchitektPowiadomienia = React.lazy(
  () => import("./pages/Architect/Notifications/Index.js"),
);
const ArchitektUsers = React.lazy(
  () => import("./pages/Architect/Users/Index.js"),
);
const ArchitektWykonawcy = React.lazy(
  () => import("./pages/Architect/Wykonawcy/Index.js"),
);
const ArchitektUser = React.lazy(
  () => import("./pages/Architect/User/Index.js"),
);
const ArchitektWykonawca = React.lazy(
  () => import("./pages/Architect/Wykonawca/Index.js"),
);
const ArchitektPracownicy = React.lazy(
  () => import("./pages/Architect/Pracownicy/Index.js"),
);
const ArchitektPracownik = React.lazy(
  () => import("./pages/Architect/Pracownik/Index.js"),
);
const ArchitektPracownikPrzypisz = React.lazy(
  () => import("./pages/Architect/UserProjectPrzypisz/Index.js"),
);
const ArchitektFinanse = React.lazy(
  () => import("./pages/Architect/Finanse/Index.js"),
);
const ArchitektFinanseProject = React.lazy(
  () => import("./pages/Architect/FinanseProject/Index.js"),
);
const ArchitektFormyIUmowy = React.lazy(
  () => import("./pages/Architect/FormyUmowy/Index.js"),
);
const ArchitectShops = React.lazy(
  () => import("./pages/Architect/Shops/Index.js"),
);
const ArchitektKalendarz = React.lazy(
  () => import("./pages/Architect/Scheduler/Index.js"),
);
const ArchitektKalendarzWeekly = React.lazy(
  () => import("./pages/Architect/Scheduler/MeetingsWeekly.js"),
);
const ArchitektKalendarzProjectDetail = React.lazy(
  () => import("./pages/Architect/Scheduler/ProjectDetail.js"),
);
const ArchitektKalendarzDeadlines = React.lazy(
  () => import("./pages/Architect/Scheduler/Deadlines.jsx"),
);
const ArchitektPayment = React.lazy(
  () => import("./pages/Architect/Payment/Index.js"),
);
const ArchitektThankYou = React.lazy(
  () => import("./pages/Architect/ThankYou/Index.js"),
);
const ArchitectSettingsUser = React.lazy(
  () => import("./pages/Architect/Settings/UserSettings.js"),
);

const RediHomeLanding = React.lazy(() => import("./pages/Core/LandingPage.js"));
const PrivacyPolicy = React.lazy(() => import("./pages/Core/PrivacyPolicy.js"));
const Terms = React.lazy(() => import("./pages/Core/Terms.js"));

function MagicLoginHandler() {
  useMagicLogin();
  return null;
}

function App() {
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const source = urlParams.get("source");
    if (source) {
      localStorage.setItem("source", source);
    }
  }, []);

  return (
    <Suspense fallback={""}>
      <BrowserRouter>
        <GTMTracker />
        <MagicLoginHandler />
        <ScrollToTop />
        <Routes>
          {/* Accounts */}
          <Route path="/" index element={<RediHomeLanding />} />
          <Route path="/polityka-prywatnosci" element={<PrivacyPolicy />} />
          <Route path="/regulamin" element={<Terms />} />
          <Route path="/zaloguj-sie" element={<Login />} />
          <Route path="/zarejestruj-sie" element={<Register />} />
          <Route path="/przypomnij-haslo" element={<ResetPassword />} />

          {/* CORE */}
          <Route
            path="/wiadomosci/:thread_id"
            element={<MessagesLandingPage />}
          />

          {/* LOGIN REQUIRED */}
          {/* ADMIN ROUTES - Move these BEFORE the dynamic project routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute adminRequired={true}>
                <Admin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/projekty"
            element={
              <ProtectedRoute adminRequired={true}>
                <AdminProjects />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/uzytkownicy"
            element={
              <ProtectedRoute adminRequired={true}>
                <AdminUsers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/architekci"
            element={
              <ProtectedRoute adminRequired={true}>
                <AdminArchitects />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/architekci/:user_id"
            element={
              <ProtectedRoute adminRequired={true}>
                <AdminArchitect />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/wykonawcy"
            element={
              <ProtectedRoute adminRequired={true}>
                <AdminWykonawcy />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/uzytkownicy/:user_id"
            element={
              <ProtectedRoute adminRequired={true}>
                <AdminUser />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/wykonawcy/:user_id"
            element={
              <ProtectedRoute adminRequired={true}>
                <AdminWykonawca />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/pracownicy"
            element={
              <ProtectedRoute adminRequired={true}>
                <AdminPracownicy />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/pracownicy/:user_id"
            element={
              <ProtectedRoute adminRequired={true}>
                <AdminPracownik />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/sprzedawcy"
            element={
              <ProtectedRoute adminRequired={true}>
                <AdminSellers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/sprzedawcy/:user_id"
            element={
              <ProtectedRoute adminRequired={true}>
                <AdminSeller />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/platnosci"
            element={
              <ProtectedRoute adminRequired={true}>
                <AdminPayments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/zgloszenia"
            element={
              <ProtectedRoute adminRequired={true}>
                <AdminFeedbacks />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/zgloszenia/:feedback_id"
            element={
              <ProtectedRoute adminRequired={true}>
                <AdminFeedback />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/harmonogram"
            element={
              <ProtectedRoute adminRequired={true}>
                <AdminHarmonogram />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/moje-konto"
            element={
              <ProtectedRoute adminRequired={true}>
                <AdminSettingsUser />
              </ProtectedRoute>
            }
          />

          {/* SELLER */}

          <Route
            path="/sprzedawca"
            element={
              <ProtectedRoute sellerRequired={true}>
                <Seller />
              </ProtectedRoute>
            }
          >
            <Route index element={<SellerHome />} />
            <Route path="klienci" element={<SellerClients />} />
            <Route path="rozliczenia" element={<SellerSettlements />} />
          </Route>

          {/* Architekt */}
          <Route
            path="/panel"
            element={
              <ProtectedRoute>
                <ArchitectLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Architekt />} />
            <Route
              path="dziekuje/:payment_id"
              element={<ArchitektThankYou />}
            />
            <Route path="platnosci" element={<ArchitektPayment />} />
            <Route path="dodaj" element={<ArchitektAddUser />} />
            <Route path="todo" element={<ArchitektTodo />} />
            <Route path="checklist" element={<ArchitektChecklist />} />
            <Route path="powiadomienia" element={<ArchitektPowiadomienia />} />
            <Route path="uzytkownicy" element={<ArchitektUsers />} />
            <Route path="wykonawcy" element={<ArchitektWykonawcy />} />
            <Route path="uzytkownicy/:user_id" element={<ArchitektUser />} />
            <Route path="wykonawcy/:user_id" element={<ArchitektWykonawca />} />
            <Route path="pracownicy/" element={<ArchitektPracownicy />} />
            <Route
              path="pracownicy/:user_id"
              element={<ArchitektPracownik />}
            />
            <Route
              path="pracownicy/:user_id/przypisz"
              element={<ArchitektPracownikPrzypisz />}
            />
            <Route
              path="finanse"
              element={
                <ProtectedRoute workerDisabled={true}>
                  <ArchitektFinanse />
                </ProtectedRoute>
              }
            />
            <Route
              path="finanse/:project_id"
              element={
                <ProtectedRoute workerDisabled={true}>
                  <ArchitektFinanseProject />
                </ProtectedRoute>
              }
            />

            <Route path="formy-i-umowy" element={<ArchitektFormyIUmowy />} />
            <Route path="sklepy" element={<ArchitectShops />} />
            <Route path="kalendarz" element={<ArchitektKalendarz />} />
            <Route
              path="kalendarz/konsultacje"
              element={<ArchitektKalendarzWeekly />}
            />
            <Route
              path="kalendarz/projekty"
              element={<ArchitektKalendarzProjectDetail />}
            />
            <Route
              path="kalendarz/deadliny"
              element={<ArchitektKalendarzDeadlines />}
            />
            <Route path="moje-konto" element={<ArchitectSettingsUser />} />
          </Route>

          <Route
            path="/:project_id"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Panel />} />
            <Route
              path="/:project_id/inspiracje/ankiety"
              element={
                <ProtectedRoute>
                  <Ankiety />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/inspiracje/ankiety/1"
              element={
                <ProtectedRoute>
                  <Ankieta1 />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/inspiracje/ankiety/2"
              element={
                <ProtectedRoute>
                  <Ankieta2 />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/inspiracje/ankiety/3"
              element={
                <ProtectedRoute>
                  <Ankieta3 />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/inspiracje/ankiety/3/wynik"
              element={
                <ProtectedRoute>
                  <Ankieta3Wynik />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/inspiracje/:inspiration_id"
              element={
                <ProtectedRoute>
                  <InspirationsDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/komunikator"
              element={
                <ProtectedRoute>
                  <Komunikator />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/inspiracje"
              element={
                <ProtectedRoute>
                  <Inspieracje />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/projekt"
              element={
                <ProtectedRoute>
                  <Projekt />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/projekt/lista-zakupow"
              element={
                <ProtectedRoute>
                  <ProjektListaZakupow />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/projekt/rysunki-techniczne"
              element={
                <ProtectedRoute>
                  <ProjektRysunkiTechniczne />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/projekt/rysunki-techniczne/:listing_id"
              element={
                <ProtectedRoute>
                  <ProjektRysunkiTechniczneDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/projekt/rysunki-techniczne/:listing_id/:image_id"
              element={
                <ProtectedRoute>
                  <ProjektRysunkiTechniczneDetailImage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/projekt/sprzet-agd"
              element={
                <ProtectedRoute>
                  <ProjektSprzetAGD />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/projekt/uklad-funkcji"
              element={
                <ProtectedRoute>
                  <ProjektUkladFunkcji />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/projekt/uklad-funkcji/:listing_id"
              element={
                <ProtectedRoute>
                  <ProjektUkladFunkcjiDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/projekt/uklad-funkcji/:listing_id/:image_id"
              element={
                <ProtectedRoute>
                  <ProjektUkladFunkcjiDetailImage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/projekt/wizualizacja"
              element={
                <ProtectedRoute>
                  <ProjektWizualizacja />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/projekt/wizualizacja/:listing_id"
              element={
                <ProtectedRoute>
                  <ProjektWizualizacjaDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/projekt/wizualizacja/:listing_id/:image_id"
              element={
                <ProtectedRoute>
                  <ProjektWizualizacjaDetailImage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/realizacja"
              element={
                <ProtectedRoute>
                  <Realizacja />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/realizacja/poprawki"
              element={
                <ProtectedRoute workerDisabled={true}>
                  <RealizacjaPoprawki />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/realizacja/poprawki/:poprawka_id"
              element={
                <ProtectedRoute workerDisabled={true}>
                  <RealizacjaPoprawkiDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/realizacja/zakres-prac"
              element={
                <ProtectedRoute workerDisabled={true}>
                  <RealizacjaZakresPrac />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/realizacja/wycena"
              element={
                <ProtectedRoute workerDisabled={true}>
                  <RealizacjaWycena />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/budzet-i-wydatki/materialy"
              element={
                <ProtectedRoute workerDisabled={true}>
                  <BudgetExpensesMaterialy />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/budzet-i-wydatki/wykonawcy"
              element={
                <ProtectedRoute workerDisabled={true}>
                  <BudgetExpensesWykonawcy />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/dokumenty"
              element={
                <ProtectedRoute workerDisabled={true}>
                  <Dokumenty />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/dokumenty/wykonawcy"
              element={
                <ProtectedRoute workerDisabled={true}>
                  <DokumentyWykonawcy />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/dokumenty/wyposazenie"
              element={
                <ProtectedRoute workerDisabled={true}>
                  <DokumentyWyposazenie />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/dokumenty/inne"
              element={
                <ProtectedRoute workerDisabled={true}>
                  <DokumentyGwarancje />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/kontakt"
              element={
                <ProtectedRoute workerDisabled={true}>
                  <KontaktPanel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/zadania"
              element={
                <ProtectedRoute>
                  <Tasks />
                </ProtectedRoute>
              }
            />

            <Route
              path="/:project_id/moje-konto"
              element={
                <ProtectedRoute>
                  <PanelUserSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/zdjecia"
              element={
                <ProtectedRoute>
                  <Zdjecia />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/zdjecia/:listing_id"
              element={
                <ProtectedRoute>
                  <ZdjeciaDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/zdjecia/:listing_id/:image_id"
              element={
                <ProtectedRoute>
                  <ZdjeciaDetailImage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/budzet-i-wydatki"
              element={
                <ProtectedRoute>
                  <BudgetExpenses />
                </ProtectedRoute>
              }
            />
            <Route
              path="/:project_id/budzet-i-wydatki/zakupy"
              element={
                <ProtectedRoute>
                  <BudgetExpensesZakupy />
                </ProtectedRoute>
              }
            />
          </Route>

          <Route path="/server-error" element={<ServerError />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
    </Suspense>
  );
}

export default App;
